/**
 * A component used to show the sessions history.
 * @author Rajkumar.N
 */
import React from "react";
import { API_URL, ERROR_HANDLE } from "config/constants";
import { toast } from "react-toastify";
import axios from "axios";

import Pagination from "components/Pagination";
import Button from "components/UI/Button";
import EditSession from "../UsersScreen/SingleUser/Modals/EditSession";
import { formatDate, formatTime } from "../../../common/Utils"
import moment from "moment";
import { Constants } from "../../../common/constants";

const CURRENT_SESSION_TEXT = "Current Session";
const SESSION_HISTORY_TEXT = "Session History";
const CHECKIN_TIME_TEXT = "Checkin Time: ";
const CHECKOUT_TIME_TEXT = "Checkout Time: ";
const MINUTES_SPENT_TEXT = "Minutes Spend: ";
const COST_TEXT = "Cost: $";
const GYM_TEXT = "Gym: "
const DATE_TEXT = "Date: "
const EDIT_TEXT = "EDIT"
const CHECKOUT_TEXT = "CHECKOUT"
const USER_NAME = "Name: ";
const USER_EMAIL = "Email: ";
const SESSION_ID = "Session ID: ";
const MSG_NO_SESSIONS = "No sessions available for the selected period.";
const SESSION_DATE_FORMAT = "YYYY/MM/DD";

const style = {
    body: {
        backgroundColor: "rgba(163, 163, 163, 0.18)",
        margin: "25px",
        marginBottom: "0px",
        boxShadow: "rgba(0, 0, 0, 0.35) 0px 2px 5px",
        padding: "25px 0px"
    },
    profilePic: {
        height: "100px",
        width: "100px",
        objectFit: "cover",
        borderRadius: "100%"
    },
    bodyTitle: { fontSize: "36px", marginTop: "0px" },
    wrapper: {
        display: "flex",
        justifyContent: "space-between",
        border: "1px solid #ccc",
        margin: "10px 50px",
        padding: "20px",
        backgroundColor: "#e1e1e154"
    },
    textContent: {
        textAlign: "left",
        fontSize: "20px"
    },
    contentWrapper: {
        flex: 1,
        padding: "0px 25px"
    },
    profileContentWrapper: {
        flex: 1,
        alignSelf: 'center',
        padding: "0px 25px"
    },
    editButton: {
        alignSelf: 'center',
        width: "150px",
        fontSize: "20px"
    }
};

/**
 *  A component used to show the sessions history.
 */
export default class SessionHistory extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            startDate: props.startDate,
            endDate: props.endDate,
            fetched: false,
            sessions: [],
            currentPage: 0,
            total_pages: 0,
            rowCount: Constants.DEFAULT_PAGINATION_COUNT,
            currentSession: {},
            showEditDialog: false,
            editSession: {}
        };
    }

    componentDidMount() {
        this.getSessionAPI();
    }

    componentWillReceiveProps(newProps) {
        this.setState({
            startDate: newProps.startDate,
            endDate: newProps.endDate,
            sessions: [],
            currentPage: 0,
            total_pages: 0,

        }, () => {
            this.getSessionAPI();
        });
    }

    /**
     * Get the sessions details.
     * @param {boolean} reset true if the rowcount is changed.
     */
    getSessionDetails(reset = false) {
        let itemsSize = this.state.currentPage * this.state.rowCount;
        if (reset) {
            // Reset the session values.
            this.setState(
                {
                    ...this.state,
                    sessions: [],
                    currentPage: 0,
                    total_pages: 0,
                }, () => {
                    this.getSessionAPI();
                }
            );
        }
        else if (!(this.state.sessions.length > itemsSize)) {
            this.getSessionAPI();
        }
    }

    /**
     * Hit the /admin/sessions API and append the session details.
     */
    getSessionAPI() {
        const toastId = toast("Fetching User Data...");
        const access_token = localStorage.getItem("access_token");
        let url = `${API_URL}/admin/sessions?limit=` + this.state.rowCount + '&page=' + this.state.currentPage;
        // Add the date filter 
        if (this.state.startDate && this.state.endDate) {
            let start = moment(this.state.startDate).format(SESSION_DATE_FORMAT);
            let end = moment(this.state.endDate).format(SESSION_DATE_FORMAT);
            url = url + '&from=' + start + '&to=' + end
        }
        axios
            .get(
                url,
                {
                    headers: {
                        "x-access-token": access_token
                    }
                }
            )
            .then(res => {
                toast.dismiss(toastId);
                this.updateWithNewData(res);
            })
            .catch(err => {
                ERROR_HANDLE(err);
            });
    }

    /**
     * Update the session details with new data.
     * @param {JSON} response the respons from the api result for /admin/sessions/
     */
    updateWithNewData(response) {
        let sessions = this.state.sessions.concat(response.data.data);
        this.setState({
            ...this.state,
            fetched: true,
            total_pages: response.data.pagination.total_pages,
            sessions: sessions
        });
    }

    /**
    * @description this is the props method gets handled from close action for the preview.
    *
    */
    close() {
        this.setState({
            showEditDialog: false
        })
    }

    /**
     * @description this is the props method gets handled from edit session component to update the session edited.
     * @param session - the session response received from edit session component
     */
    updateSession = (session) => {
        const sessions = this.state.sessions;
        if (sessions && sessions[0]) {
            let sessionArray = [...this.state.sessions]; // make a separate copy of the sessions
            sessionArray.forEach((sessionValue) => {
                if (sessionValue.id == session.id) {
                    sessionValue.checkout_time = session.checkout_time
                    sessionValue.cost = session.cost / 100
                    sessionValue.minutes_spend = session.minutes_spend / (1000 * 60) //converting milliseconds to minutes.
                }
            })
            this.setState({
                ...this.state,
                sessions: sessionArray,
                showEditDialog: false
            })
        }
    }

    /**
     * @description this is the props method gets handled from edit session component to update the current session.
     * 
     */
    updateCurrentSession = () => {
        this.setState({
            fetched: false,
            currentPage: 0,
            currentSession: {},
            showEditDialog: false,
            editSession: {}
        }, () => this.getSessionDetails(true))
    }

    /**
     * Render the session component for each session item.
     * @param {JSON} item the JSON represents the session item.
     * @param {String} buttonTitle the string refers the action button text.
     */
    renderSessionComponent(item, buttonTitle) {
        let user = item.user;
        let checkin_time = formatTime(item.checkin_time, item.activity_timezone);
        let checkout_time = formatTime(item.checkout_time, item.activity_timezone);
        return (
            <div style={style.wrapper} key={"session " + item.id}>
                <div style={style.profileContentWrapper}>
                    <img
                        src={
                            user.profile_picture
                                ? user.profile_picture
                                : "https://via.placeholder.com/250"
                        }
                        alt={"Picture" + user.id}
                        style={style.profilePic}
                    />
                </div>
                <div style={style.contentWrapper}>
                    <p style={style.textContent}>{USER_NAME + user.name}</p>
                    <p style={style.textContent}>
                        {DATE_TEXT + formatDate(item.date, item.activity_timezone).toDateString()}
                    </p>
                    <p style={style.textContent}>{GYM_TEXT + item.gym}</p>


                </div>
                <div style={style.contentWrapper}>
                    <p style={style.textContent}>{USER_EMAIL + user.email}</p>
                    <p style={style.textContent}>
                        {CHECKIN_TIME_TEXT}
                        {`${checkin_time ? checkin_time : '--'}`}
                    </p>
                    <p style={style.textContent}>
                        {CHECKOUT_TIME_TEXT}
                        {`${checkout_time ? checkout_time : '--'}`}
                    </p>
                </div>
                <div style={style.contentWrapper}>
                    <p style={style.textContent}>
                        {MINUTES_SPENT_TEXT + item.minutes_spend}
                    </p>
                    <p style={style.textContent}>{COST_TEXT + item.cost}</p>
                    <p style={style.textContent}>{SESSION_ID + item.id}</p>
                </div>
                <Button
                    style={style.editButton}
                    type="primary"
                    onClick={() =>
                        this.setState({
                            editSession: item,
                            showEditDialog: true
                        })
                    }
                >
                    {buttonTitle}
                </Button>
            </div>
        );
    }

    /**
     * Render the session details with the pagination.
     */
    renderSession() {
        const rowCount = parseInt(this.state.rowCount)
        const sessionHistory = this.state.fetched && this.state.sessions.length > 0
            ? this.state.sessions.map((item, i) => {
                if (
                    i >= this.state.currentPage * rowCount &&
                    i < this.state.currentPage * rowCount + rowCount
                ) {
                    return (
                        this.renderSessionComponent(item, item.checkout_time ? EDIT_TEXT : CHECKOUT_TEXT)
                    );
                }
            })
            : MSG_NO_SESSIONS;

        const currentSession = this.state.currentSession.checkin_time
            ? <div style={style.body}>
                <p style={style.bodyTitle}>{CURRENT_SESSION_TEXT}</p>
                {this.renderSessionComponent(this.state.currentSession, CHECKOUT_TEXT)}
            </div>
            : "";

        return (
            <div>
                {currentSession}
                <div style={style.body}>
                    <p style={style.bodyTitle}>{SESSION_HISTORY_TEXT}</p>
                    {sessionHistory}
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                    {this.state.fetched ? (
                        <Pagination
                            currentPage={this.state.currentPage}
                            // pageCount={this.state.sessions.length / rowCount}
                            pageCount={this.state.total_pages}
                            rowCount={rowCount}
                            pageAction={pageNumber => {
                                this.setState({ ...this.state, currentPage: pageNumber }, () => {
                                    this.getSessionDetails();
                                });
                            }}
                            updateRowCount={(rowCount) => {
                                this.setState({ rowCount: rowCount }, () => {
                                    this.getSessionDetails(true);
                                });
                            }}
                        />
                    ) : (
                            ""
                        )}
                </div>
                {
                    this.state.showEditDialog &&
                    <EditSession
                        sessionId={this.state.editSession.id}
                        checkInTime={formatDate(this.state.editSession.checkin_time, this.state.editSession.activity_timezone)}
                        checkOutTime={formatDate(this.state.editSession.checkout_time, this.state.editSession.activity_timezone)}
                        actualCheckoutTime={formatDate(this.state.editSession.actual_checkout, this.state.editSession.activity_timezone)}
                        cost={this.state.editSession.cost}
                        minutesSpent={this.state.editSession.minutes_spend}
                        timeZone={this.state.editSession.activity_timezone}
                        close={() => this.close()}
                        updateSession={(session) => this.updateSession(session)}
                        updateCurrentSession={() => this.updateCurrentSession()}
                    />
                }
            </div >
        );
    }

    render() {
        return (
            this.renderSession()
        );
    }
}
